import { Box, Container, Flex } from "@mantine/core";

import { renderTree } from "@mountayaapp/markdoc/renderers";

import { resolveTab } from "./content/registry";
import { resolveDoc } from "./content/resolveDoc";
import { DocsAppShell } from "./layout/DocsAppShell";
import { DocsBreadcrumbs } from "./layout/DocsBreadcrumbs";
import { DocsLlmActions } from "./layout/DocsLlmActions";
import { DocsNotFound } from "./layout/DocsNotFound";
import { DocsPager } from "./layout/DocsPager";
import { DocsToc } from "./layout/DocsToc";
import { Changelog } from "./markdoc/components/Changelog";
import { GraphiQL } from "./markdoc/components/GraphiQL";
import { Redoc } from "./markdoc/components/Redoc";
import { Studio } from "./markdoc/components/Studio";

/**
 * docComponents are the heavy, environment-dependent renderers the documentation
 * app injects into renderTree. They live here, not in the shared markdoc package,
 * so its dependency graph stays light: GraphiQL and Redoc are code-split and
 * client-only; the Studio embed is a static iframe; the Changelog renders its
 * sample history from local data.
 */
const docComponents = { Changelog, GraphiQL, Redoc, Studio };

/**
 * AppProps carry the pathname the page renders for. The prerender step passes each
 * page's route; the client passes the live `window.location.pathname`, so the same
 * component resolves content identically during the static build and after
 * hydration.
 */
interface AppProps {
  pathname: string;
}

/**
 * READING_WIDTH caps a bare reading page (the changelog) at a comfortable prose measure.
 * A sidebar page's column is narrowed by the navbar and table-of-contents rail; a bare
 * page has neither, so without this cap its column would run far wider than the reading
 * measure the rest of the documentation uses.
 */
const READING_WIDTH = 640;

/**
 * App resolves the authored page for a pathname and renders it inside the docs
 * shell, framed by the header tab that owns the pathname. A sidebar-layout tab
 * (Documentation, Tools) renders the page with its breadcrumbs, table-of-contents
 * rail, and prev/next pager; a bare-layout tab (the interactive API surfaces, the
 * changelog) drops that chrome and holds its body to a reading column, since it has no
 * sidebar or rail to set the measure the way a sidebar page does. The page title is the
 * document's own first heading, so no separate title is drawn; a page sets `toc: false`
 * to drop the rail. A page sets `full: true` to drop the content container and chrome
 * entirely and render its body edge-to-edge, so an embedded explorer fills the viewport
 * below the header. An unmatched location renders the not-found view under the header
 * alone (also emitted as the static `error.html` the host serves as its error document).
 */
export const App = ({ pathname }: AppProps) => {
  const { doc, lang } = resolveDoc(pathname);
  const tab = resolveTab(pathname);
  const bare = tab.layout !== "sidebar";

  // A location with no page belongs to no section, so the not-found view is framed
  // bare whichever tab the pathname fell back to: the header alone over a full-width
  // body, with no sidebar railing a reading order this location is not part of.
  if (!doc) {
    return (
      <DocsAppShell pathname={pathname} tab={{ ...tab, layout: "bare" }}>
        <DocsNotFound />
      </DocsAppShell>
    );
  }

  // A full-bleed page is just its body: no container, no padding, no breadcrumbs or
  // pager, so the embedded explorer fills the viewport below the header.
  if (doc.frontmatter.full) {
    return (
      <DocsAppShell full pathname={pathname} tab={tab}>
        {renderTree(doc.tree, { components: docComponents })}
      </DocsAppShell>
    );
  }

  const showToc = doc.frontmatter.toc !== false;

  return (
    <DocsAppShell
      aside={showToc ? <DocsToc entries={doc.toc} /> : undefined}
      pathname={pathname}
      tab={tab}
    >
      <Container pb="xl" pt="md" size={bare ? READING_WIDTH : "md"}>
        {bare ? null : (
          <DocsBreadcrumbs lang={lang} pathname={pathname} title={doc.frontmatter.title} />
        )}
        {bare ? (
          renderTree(doc.tree, { components: docComponents })
        ) : (
          <Box display="flow-root" pos="relative">
            <Flex
              align="center"
              h="calc(var(--mantine-h1-line-height) * var(--mantine-h1-font-size))"
              pos="absolute"
              right={0}
              top="var(--mantine-spacing-lg)"
              visibleFrom="sm"
            >
              <DocsLlmActions pathname={pathname} />
            </Flex>
            {renderTree(doc.tree, { components: docComponents })}
          </Box>
        )}
        {bare ? null : <DocsPager lang={lang} pathname={pathname} />}
      </Container>
    </DocsAppShell>
  );
};
